import { useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import {
  MDXEditor,
  headingsPlugin,
  listsPlugin,
  quotePlugin,
  linkPlugin,
  markdownShortcutPlugin,
  toolbarPlugin,
  UndoRedo,
  BoldItalicUnderlineToggles,
  ListsToggle,
  Separator,
} from '@mdxeditor/editor';
import '@mdxeditor/editor/style.css';
import './markdown-editor.css';

export const MarkdownEditor = ({ value, onChange, placeholder, minRows = 2, label, onSubmit }) => {
  const theme = useTheme();
  const colorMode = theme.palette.mode;
  const [rich, setRich] = useState(false);

  const toggle = () => setRich((v) => !v);

  return (
    <Stack spacing={0.5}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        {label ? (
          <Typography
            color="text.secondary"
            sx={{ fontSize: 12, fontWeight: 500 }}
          >
            {label}
          </Typography>
        ) : (
          <Box />
        )}
        <Tooltip title={rich ? 'Modo simple' : 'Editor de formato (markdown)'}>
          <IconButton
            size="small"
            color={rich ? 'primary' : 'default'}
            onClick={toggle}
            sx={{ p: 0.5 }}
          >
            <FormatBoldIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      {rich ? (
        <div className={colorMode === 'dark' ? 'dark' : 'light'}>
          <Box
            sx={{
              '& .mdxeditor': {
                position: 'relative',
                zIndex: 1,
                fontSize: 13,
                borderRadius: 1,
                border: 1,
                borderColor: 'divider',
                boxShadow: 'none',
              },
              '& .mdxeditor-toolbar': {
                borderTop: 0,
                borderLeft: 0,
                borderRight: 0,
                overflow: 'visible',
              },
              '& .mdx-content': {
                minHeight: Math.max(80, minRows * 26),
                fontSize: 13,
              },
            }}
          >
            <MDXEditor
              markdown={value || ''}
              onChange={(md) => onChange(md)}
              placeholder={placeholder}
              contentEditableClassName="mdx-content"
              plugins={[
                headingsPlugin(),
                listsPlugin(),
                quotePlugin(),
                linkPlugin(),
                markdownShortcutPlugin(),
                toolbarPlugin({
                  toolbarClassName: 'mdxeditor-toolbar',
                  toolbarContents: () => (
                    <>
                      <UndoRedo />
                      <Separator />
                      <BoldItalicUnderlineToggles options={['Bold', 'Italic']} />
                      <Separator />
                      <ListsToggle options={['bullet', 'number']} />
                    </>
                  ),
                }),
              ]}
            />
          </Box>
        </div>
      ) : (
        <TextField
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (onSubmit && e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              onSubmit();
            }
          }}
          placeholder={placeholder}
          multiline
          minRows={Math.max(1, minRows)}
          fullWidth
        />
      )}
    </Stack>
  );
};
